import { useCallback, type FC } from "react";

import { usePagination, useRootDispatch, useRootSelector, useTableDataSorting } from "@ds/hooks";

import { MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { Loading } from "@ds/components/loaders/loading";
import { TableWithPagination } from "@ds/components/table/main-table";
import {
  Column,
  getEntityIdColumn,
  getEntityNameColumn,
  getGoToDetailsColumn,
  getLastUpdatedColumn,
  getSelectionColumn,
} from "@ds/components/table/table-columns";
import { TotalSelectedLabel } from "@ds/components/table/total-selected-label";

import { formatMixingStrategy } from "../utils/helpers";
import { selectCampaignsIsLoading, selectSelectedCampaigns, selectTableCampaigns } from "../redux/selectors";
import { CampaignsRemoteOperation, campaignsActions } from "../redux/slice";

const getMixingStrategyBodyTemplate = (campaign: Campaign) => formatMixingStrategy(campaign.mixing_strategy);

const getItemCountBodyTemplate = (campaign: Campaign) => campaign.campaign_items_count ?? 0;

export const CampaignsTable: FC = () => {
  const selectedEntities = useRootSelector(selectSelectedCampaigns);
  const entities = useRootSelector(selectTableCampaigns);

  const isLoading = useRootSelector(selectCampaignsIsLoading);
  const checkboxIsLoading = useRootSelector(state =>
    selectCampaignsIsLoading(state, CampaignsRemoteOperation.CHECKBOXES),
  );

  const [pagination, setPagination] = usePagination(MainTableDataTypeEnum.ContentCampaigns);
  const [{ sortOrder, sortField }, setSorting] = useTableDataSorting(MainTableDataTypeEnum.ContentCampaigns);

  const dispatch = useRootDispatch();
  const onToggleRowSelected = useCallback(
    (entry: Campaign) => dispatch(campaignsActions.toggleSelected(entry.id)),
    [dispatch],
  );

  const onSingleRowSelectHandler = useCallback(
    (entry: Campaign) => {
      dispatch(campaignsActions.selectCampaigns([entry]));
    },
    [dispatch],
  );

  const onToggleAllHandler = useCallback(
    (isSelectedAll: boolean) => {
      if (isSelectedAll) {
        dispatch(campaignsActions.fetchCampaigns(undefined, undefined, CampaignsRemoteOperation.CHECKBOXES));
      } else {
        dispatch(campaignsActions.selectCampaigns([]));
      }
    },
    [dispatch],
  );

  return (
    <Loading isLoading={isLoading}>
      <TableWithPagination
        value={entities}
        selection={selectedEntities}
        onSingleRowSelect={onSingleRowSelectHandler}
        toggleRowSelected={onToggleRowSelected}
        loading={isLoading}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={setSorting}
        pagination={pagination}
        onPageChange={setPagination}
      >
        {getSelectionColumn({
          total: pagination.total,
          selected: selectedEntities.length,
          isLoading: checkboxIsLoading,
          onToggleAll: onToggleAllHandler,
        })}

        {getEntityIdColumn()}
        {getEntityNameColumn({ style: { flexGrow: 2 } })}

        <Column
          field="mixing_strategy"
          header="Mixing Strategy"
          body={getMixingStrategyBodyTemplate}
          style={{ flexGrow: 1 }}
          resizeable={false}
          sortable
        />

        <Column
          field="campaign_items_count"
          header="# Items"
          body={getItemCountBodyTemplate}
          className="center w100"
          resizeable={false}
          sortable
        />

        {getLastUpdatedColumn()}
        {getGoToDetailsColumn()}
      </TableWithPagination>

      <TotalSelectedLabel selectedCount={checkboxIsLoading ? Number(pagination.total) : selectedEntities.length} />
    </Loading>
  );
};
